import { AxiosError } from 'axios';
import useForm from 'antd/es/form/hooks/useForm';
import {
    CommonFormProps,
    GroupConsultationFormFields,
    FormModeDependedTexts,
    JournalId,
} from 'types';
import { AppForm, AppFormProps, ChildrenWithParentsPicker } from 'components/organisms';
import {
    DatePickerElement,
    DescriptionInputElement,
    FormNavButtons,
    SubjectInputElement,
} from 'components/molecules';
import { commonTexts, journalTexts } from 'consts/text';
import { useDependentFormFields } from 'hooks/useDependentFormFields/useDependentFormFields';
import { formFields } from 'consts/form/formFields';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';

const addModeTexts: FormModeDependedTexts = {
    name: 'groupConsultationAdd',
    title: journalTexts.journalDetails.common.consultations.addConsultation,
    buttonText: commonTexts.actionLabels.add,
};

const editModeTexts: FormModeDependedTexts = {
    name: 'groupConsultationEdit',
    title: journalTexts.journalDetails.common.consultations.editConsultation,
    buttonText: commonTexts.actionLabels.save,
};

type ConsultationAddEditFormProps = Omit<
    AppFormProps<GroupConsultationFormFields>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> &
    Pick<CommonFormProps, 'mode'> & {
        journalId: JournalId;
        axiosError?: AxiosError | null;
        isLoading?: boolean;
        onFinish: (consultationData: GroupConsultationFormFields) => void;
    };
export const GroupJournalConsultationAddEditForm = ({
    mode,
    form,
    initialValues,
    journalId,
    axiosError,
    ...props
}: ConsultationAddEditFormProps) => {
    const [formInstance] = useForm<GroupConsultationFormFields>(form);
    const {
        watchedFieldsValues: watchedDateValue,
        handleWatchedFieldsValuesChange: handleWatchedDateValueChange,
    } = useDependentFormFields({
        watchedFields: [formFields.date],
        dependentFields: [formFields.childrenWithParentsIds],
        formInstance,
    });

    const isAddMode = mode === 'add';
    const { name, title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;
    const { date: dateValue } = watchedDateValue;
    const parsedDate = dateValue ? parseDayjsDateIntoString(dateValue, 'YYYY-MM-DD') : undefined;

    const consultationAddEditError = axiosError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppForm<GroupConsultationFormFields>
            form={formInstance}
            name={name}
            title={title}
            error={consultationAddEditError}
            withGoBack
            {...props}
        >
            <SubjectInputElement
                label={journalTexts.journalDetails.common.consultations.consultationSubject}
            />
            <DatePickerElement isHalfParentWidth onChange={handleWatchedDateValueChange} />
            <DescriptionInputElement />
            <ChildrenWithParentsPicker
                journalId={journalId}
                date={parsedDate}
                disabled={!parsedDate}
            />
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={buttonText}
                leftIcon={buttonIcon}
                withPlus={isAddMode}
                withDeviceFloppy={!isAddMode}
            />
        </AppForm>
    );
};
